import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';
import { FiMessageSquare, FiSearch } from 'react-icons/fi';
import { fetchRuns } from '../services/api';

const QueryAnalytics = () => {
  const [queryData, setQueryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadQueryData = useCallback(async () => {
    try {
      const runsResponse = await fetchRuns();
      // Handle both cases: when fetchRuns returns an array directly or an object with runs property
      const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);

      // Extract all queries
      const allQueries = runs
        .filter(run => run.llm_calls && run.llm_calls.length > 0)
        .map(run => ({
          prompt: run.llm_calls[0].prompt || '',
          response: run.llm_calls[0].response || '',
          inputTokens: run.llm_calls[0].input_tokens || 0,
          outputTokens: run.llm_calls[0].output_tokens || 0,
          latency: run.llm_calls[0].latency_ms || 0,
          model: run.llm_calls[0].model || 'unknown',
          timestamp: run.start_time
        }));

      // Calculate metrics
      const avgQueryLength = allQueries.reduce((sum, q) => sum + q.inputTokens, 0) / allQueries.length || 0;
      const avgResponseLength = allQueries.reduce((sum, q) => sum + q.outputTokens, 0) / allQueries.length || 0;
      
      // Query length distribution
      const lengthDistribution = calculateLengthDistribution(allQueries);
      
      // Response time vs length correlation - convert to bar chart data
      const correlationData = calculateResponseTimeBins(allQueries);

      // Query performance analysis
      const queryPerformance = calculateQueryPerformance(runs);
      
      // Model performance comparison
      const modelPerformance = calculateModelPerformance(allQueries);

      setQueryData({
        totalQueries: allQueries.length,
        avgQueryLength: Math.round(avgQueryLength),
        avgResponseLength: Math.round(avgResponseLength),
        allQueries,
        lengthDistribution,
        correlationData,
        queryPerformance,
        modelPerformance
      });
    } catch (error) {
      console.error('Error loading query data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueryData();
  }, [loadQueryData]);

  const calculateLengthDistribution = (queries) => {
    const ranges = [
      { name: '0-50', min: 0, max: 50, count: 0 },
      { name: '51-100', min: 51, max: 100, count: 0 },
      { name: '101-200', min: 101, max: 200, count: 0 },
      { name: '201-500', min: 201, max: 500, count: 0 },
      { name: '500+', min: 501, max: Infinity, count: 0 }
    ];

    queries.forEach(q => {
      const tokens = q.inputTokens;
      const range = ranges.find(r => tokens >= r.min && tokens <= r.max);
      if (range) range.count++;
    });

    return ranges;
  };

  const calculateResponseTimeBins = (queries) => {
    // Group queries by token count ranges and calculate average latency
    const tokenRanges = [
      { name: '0-100', min: 0, max: 100, queries: [] },
      { name: '101-200', min: 101, max: 200, queries: [] },
      { name: '201-500', min: 201, max: 500, queries: [] },
      { name: '501-1000', min: 501, max: 1000, queries: [] },
      { name: '1000+', min: 1001, max: Infinity, queries: [] }
    ];

    queries.forEach(q => {
      const totalTokens = q.inputTokens + q.outputTokens;
      const range = tokenRanges.find(r => totalTokens >= r.min && totalTokens <= r.max);
      if (range) range.queries.push(q);
    });

    return tokenRanges.map(range => ({
      tokenRange: range.name,
      avgLatency: range.queries.length > 0 
        ? Math.round(range.queries.reduce((sum, q) => sum + q.latency, 0) / range.queries.length)
        : 0,
      queryCount: range.queries.length
    }));
  };

  const determineBestChartType = (data) => {
    if (!data || data.length < 3) return 'bar';
    
    // Calculate the trend (linear regression slope)
    const n = data.length;
    const xValues = data.map((_, index) => index);
    const yValues = data.map(d => d.avgLatency);
    
    const sumX = xValues.reduce((a, b) => a + b, 0);
    const sumY = yValues.reduce((a, b) => a + b, 0);
    const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
    const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    
    // Calculate R-squared to determine how well the data fits a line
    const meanY = sumY / n;
    const ssRes = yValues.reduce((sum, y, i) => {
      const predicted = slope * xValues[i] + (sumY / n - slope * sumX / n);
      return sum + Math.pow(y - predicted, 2);
    }, 0);
    const ssTot = yValues.reduce((sum, y) => sum + Math.pow(y - meanY, 2), 0);
    const rSquared = 1 - (ssRes / ssTot);
    
    // If R-squared > 0.7 and slope is significant, use line chart
    // Otherwise use bar chart for better readability
    return rSquared > 0.7 && Math.abs(slope) > 0.1 ? 'line' : 'bar';
  };

  const calculateQueryPerformance = (runs) => {
    // Calculate query performance metrics from runs data
    const performanceData = [];
    
    runs.forEach(run => {
      if (run.llm_calls && run.llm_calls.length > 0) {
        run.llm_calls.forEach(call => {
          const model = call.model || 'unknown';
          const status = run.status || 'unknown';
          const executionTime = run.execution_duration || 0;
          
          // Calculate success rate
          const isSuccess = status === 'success';
          
          // Calculate performance metrics
          const throughput = call.tokens_per_second || 0;
          const outputThroughput = call.output_throughput || 0;
          const totalThroughput = call.total_throughput || 0;
          
          performanceData.push({
            model,
            status,
            isSuccess: isSuccess ? 1 : 0,
            executionTime: Math.round(executionTime * 100) / 100,
            latency: Math.round(call.latency_ms || 0),
            throughput: Math.round(throughput * 100) / 100,
            outputThroughput: Math.round(outputThroughput * 100) / 100,
            totalThroughput: Math.round(totalThroughput * 100) / 100,
            inputTokens: call.input_tokens || 0,
            outputTokens: call.output_tokens || 0,
            totalTokens: call.total_tokens || 0
          });
        });
      }
    });
    
    // Group by model and calculate averages
    const modelStats = {};
    performanceData.forEach(item => {
      if (!modelStats[item.model]) {
        modelStats[item.model] = {
          model: item.model,
          totalQueries: 0,
          successfulQueries: 0,
          avgLatency: 0,
          avgThroughput: 0,
          avgExecutionTime: 0,
          totalTokens: 0,
          successRate: 0
        };
      }
      
      modelStats[item.model].totalQueries++;
      modelStats[item.model].successfulQueries += item.isSuccess;
      modelStats[item.model].avgLatency += item.latency;
      modelStats[item.model].avgThroughput += item.throughput;
      modelStats[item.model].avgExecutionTime += item.executionTime;
      modelStats[item.model].totalTokens += item.totalTokens;
    });
    
    // Calculate averages and success rates
    Object.values(modelStats).forEach(stat => {
      stat.avgLatency = Math.round(stat.avgLatency / stat.totalQueries);
      stat.avgThroughput = Math.round((stat.avgThroughput / stat.totalQueries) * 100) / 100;
      stat.avgExecutionTime = Math.round((stat.avgExecutionTime / stat.totalQueries) * 100) / 100;
      stat.successRate = Math.round((stat.successfulQueries / stat.totalQueries) * 100);
    });

    return Object.values(modelStats)
      .sort((a, b) => b.successRate - a.successRate)
      .slice(0, 6) // Show top 6 models
      .map(stat => ({
        ...stat,
        model: stat.model === 'gpt-4o-mini-2024-07-18' ? 'GPT-4o Mini' : stat.model
      }));
  };

  const calculateModelPerformance = (queries) => {
    // Group queries by model and calculate performance metrics
    const modelStats = {};
    
    queries.forEach(q => {
      const model = q.model || 'unknown';
      if (!modelStats[model]) {
        modelStats[model] = {
          model,
          totalQueries: 0,
          totalTokens: 0,
          totalLatency: 0,
          avgLatency: 0,
          avgTokens: 0,
          successRate: 100 // Assuming all queries are successful
        };
      }
      
      modelStats[model].totalQueries++;
      modelStats[model].totalTokens += (q.inputTokens + q.outputTokens);
      modelStats[model].totalLatency += q.latency;
    });

    // Calculate averages
    Object.values(modelStats).forEach(stat => {
      stat.avgLatency = Math.round(stat.totalLatency / stat.totalQueries);
      stat.avgTokens = Math.round(stat.totalTokens / stat.totalQueries);
    });

    return Object.values(modelStats).sort((a, b) => b.totalQueries - a.totalQueries);
  };

  const filteredQueries = queryData?.allQueries?.filter(q => 
    q.prompt.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Don't render if no query data
  if (!loading && (!queryData || queryData.totalQueries === 0)) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiMessageSquare className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Query Data Available</h3>
          <p className="text-gray-500">Query analytics will appear here once query data is available.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
            <FiMessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Query Analytics</h2>
            <p className="text-gray-600">Analyze prompts and responses for optimization insights</p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Queries</p>
            <p className="text-2xl font-bold text-gray-900">{queryData?.totalQueries || 0}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Avg Query Length</p>
            <p className="text-2xl font-bold text-gray-900">{queryData?.avgQueryLength || 0} tokens</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Avg Response Length</p>
            <p className="text-2xl font-bold text-gray-900">{queryData?.avgResponseLength || 0} tokens</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Success Rate</p>
            <p className="text-2xl font-bold text-gray-900">100%</p>
          </div>
        </div>

        {/* Chart Legend and Instructions */}
        <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-sm">
          <div className="text-center py-6 border-b border-blue-200">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-blue-600 text-lg">📊</span>
              </div>
              <h4 className="text-lg font-bold text-gray-800">Chart Overview & Guide</h4>
            </div>
          </div>
          <div className="p-6">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                <h5 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Query Length Distribution
                </h5>
                <p className="text-sm text-gray-600 leading-relaxed ml-5">
                  Shows how many queries fall into different token count ranges, helping identify optimal query lengths for your use case.
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                <h5 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  Response Time vs Token Count
                </h5>
                <p className="text-sm text-gray-600 leading-relaxed ml-5">
                  Displays average latency for different token ranges with intelligent chart type selection for optimal readability.
                </p>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                <h5 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                  Query Success Rate & Performance
                </h5>
                <p className="text-sm text-gray-600 leading-relaxed ml-5">
                  Shows success rates, latency, and throughput metrics for different AI models, helping optimize query performance.
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                <h5 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                  Model Performance Comparison
                </h5>
                <p className="text-sm text-gray-600 leading-relaxed ml-5">
                  Compares performance metrics across different AI models, helping identify the most efficient models for your use case.
                </p>
              </div>
            </div>
          </div>
          </div>
          

        </div>
      </div>

      {/* Charts Grid - Enhanced Two Charts Per Row */}
      <div className="space-y-8">
        {/* Row 1: Query Length Distribution & Response Time vs Token Count */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Query Length Distribution */}
          <div className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-2xl shadow-xl border border-blue-100 relative hover:shadow-2xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-purple-500"></div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Query Length Distribution</h3>
              <p className="text-xs text-gray-600">Token count ranges</p>
            </div>
            {/* X and Y Axis Labels */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">Y: Number of Queries</div>
            </div>
            <div className="absolute bottom-2 right-4 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">X: Token Count Range</div>
            </div>
            <div className="h-[320px] mt-12">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={queryData?.lengthDistribution || []} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                  <defs>
                    <linearGradient id="barGradient1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#1E40AF" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="barGradient2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#059669" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="barGradient3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#D97706" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="barGradient4" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#DC2626" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="barGradient5" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#7C3AED" stopOpacity={1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" opacity={0.6} />
                <XAxis 
                  dataKey="name" 
                    angle={0}
                  textAnchor="middle"
                    height={60}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                />
                <YAxis 
                    width={60}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickMargin={10}
                />
                <Tooltip 
                  formatter={(value, name) => [value, 'Number of Queries']}
                  labelFormatter={(label) => `Token Range: ${label}`}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E5E7EB', 
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(10px)'
                    }}
                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}
                  />
                  <Bar dataKey="count" name="Number of Queries" radius={[8, 8, 0, 0]} barSize={40}>
                  {queryData?.lengthDistribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`url(#barGradient${(index % 5) + 1})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

          {/* Response Time vs Token Count - Dynamic Chart Type */}
          <div className="bg-gradient-to-br from-white to-green-50 p-6 rounded-2xl shadow-xl border border-green-100 relative hover:shadow-2xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-500"></div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Response Time vs Token Count</h3>
              <p className="text-xs text-gray-600">
                Average latency
                {queryData?.correlationData && (
                  <span className="ml-1 text-green-600 font-semibold">
                    ({determineBestChartType(queryData.correlationData)})
                  </span>
                )}
              </p>
            </div>
            {/* X and Y Axis Labels */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">Y: Average Latency (ms)</div>
            </div>
            <div className="absolute bottom-2 right-4 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">X: Token Count Range</div>
            </div>
            <div className="h-[320px] mt-12">
            <ResponsiveContainer width="100%" height="100%">
                {queryData?.correlationData && determineBestChartType(queryData.correlationData) === 'line' ? (
                  <LineChart data={queryData.correlationData} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                    <defs>
                      <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#10B981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" opacity={0.6} />
                    <XAxis 
                      dataKey="tokenRange" 
                      angle={0}
                      textAnchor="middle"
                      height={60}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                      tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    />
                    <YAxis 
                      width={60}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                      tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                      tickMargin={10}
                    />
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      labelFormatter={(label) => `Token Range: ${label}`}
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(10px)'
                      }}
                      cursor={{ stroke: '#10B981', strokeWidth: 2, strokeDasharray: '5 5' }}
                    />
                    <Legend 
                      verticalAlign="top" 
                      height={36}
                      wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="avgLatency" 
                      name="Average Latency (ms)"
                      stroke="#10B981" 
                      strokeWidth={4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      dot={{ fill: '#10B981', r: 6, stroke: '#fff', strokeWidth: 3 }}
                      activeDot={{ r: 8, stroke: '#10B981', strokeWidth: 3, fill: '#fff' }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={queryData?.correlationData || []} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                    <defs>
                      <linearGradient id="barGradientLatency" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.8}/>
                        <stop offset="100%" stopColor="#059669" stopOpacity={1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" opacity={0.6} />
                <XAxis 
                  dataKey="tokenRange" 
                      angle={0}
                  textAnchor="middle"
                      height={60}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                      tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                />
                <YAxis 
                  width={60}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                      tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                      tickMargin={10}
                />
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                  labelFormatter={(label) => `Token Range: ${label}`}
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(10px)'
                      }}
                      cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }}
                />
                    <Legend 
                      verticalAlign="top" 
                      height={36}
                      wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}
                    />
                    <Bar dataKey="avgLatency" name="Average Latency (ms)" fill="url(#barGradientLatency)" radius={[8, 8, 0, 0]} barSize={40} />
              </BarChart>
                )}
            </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Row 2: Most Common Topics & Query Patterns by Hour */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Query Success Rate & Performance */}
          <div className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-2xl shadow-xl border border-purple-100 relative hover:shadow-2xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-pink-500"></div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Query Success Rate & Performance</h3>
              <p className="text-xs text-gray-600">Success rates and performance metrics</p>
            </div>
            {/* X and Y Axis Labels */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">Y: AI Models</div>
            </div>
            <div className="absolute bottom-2 right-4 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">X: Success Rate (%)</div>
            </div>
            <div className="h-[320px] mt-12 flex items-center justify-center px-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                  data={queryData?.queryPerformance || []} 
                layout="vertical"
                  margin={{ top: 20, right: 25, left: 120, bottom: 25 }}
                >
                  <defs>
                    <linearGradient id="successBarGradient1" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#059669" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="successBarGradient2" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#1E40AF" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="successBarGradient3" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#7C3AED" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="successBarGradient4" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#D97706" stopOpacity={1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" opacity={0.6} horizontal={true} vertical={false} />
                <XAxis 
                  type="number" 
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                />
                <YAxis 
                    dataKey="model" 
                  type="category" 
                    width={80}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickMargin={8}
                    label={{ value: 'AI Models', angle: -90, position: 'insideLeft', offset: 0, style: { fontSize: '12px', fontWeight: 'bold', fill: '#374151' } }}
                />
                <Tooltip 
                    formatter={(value, name) => [
                      name === 'successRate' ? `${value}%` : 
                      name === 'avgLatency' ? `${value}ms` : 
                      name === 'avgThroughput' ? `${value} tokens/s` : 
                      name === 'avgExecutionTime' ? `${value}s` : 
                      name === 'totalQueries' ? `${value} queries` : 
                      name === 'totalTokens' ? `${value} tokens` : value,
                      name === 'successRate' ? 'Success Rate' :
                      name === 'avgLatency' ? 'Avg Latency' :
                      name === 'avgThroughput' ? 'Avg Throughput' :
                      name === 'avgExecutionTime' ? 'Avg Execution Time' :
                      name === 'totalQueries' ? 'Total Queries' :
                      name === 'totalTokens' ? 'Total Tokens' : name
                    ]}
                    labelFormatter={(label) => `Model: ${label}`}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E5E7EB', 
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(10px)'
                    }}
                    cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }}
                />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}
                  />
                  <Bar dataKey="successRate" name="Success Rate (%)" radius={[0, 8, 8, 0]} barSize={25}>
                    {queryData?.queryPerformance?.map((entry, index) => (
                      <Cell key={`success-cell-${index}`} fill={`url(#successBarGradient${(index % 4) + 1})`} />
                    ))}
                  </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

          {/* Model Performance Comparison */}
          <div className="bg-gradient-to-br from-white to-orange-50 p-6 rounded-2xl shadow-xl border border-orange-100 relative hover:shadow-2xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-red-500"></div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Model Performance Comparison</h3>
              <p className="text-xs text-gray-600">Performance metrics by model</p>
            </div>
            {/* X and Y Axis Labels */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">Y: Average Latency (ms)</div>
            </div>
            <div className="absolute bottom-2 right-4 z-10 text-center">
              <div className="text-xs text-gray-500 font-medium">X: AI Model</div>
            </div>
            <div className="h-[320px] mt-12">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={queryData?.modelPerformance || []} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                  <defs>
                    <linearGradient id="modelBarGradient1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F97316" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#EA580C" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="modelBarGradient2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#7C3AED" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="modelBarGradient3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#059669" stopOpacity={1}/>
                    </linearGradient>
                    <linearGradient id="modelBarGradient4" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#1E40AF" stopOpacity={1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" opacity={0.6} />
                <XAxis 
                    dataKey="model" 
                    angle={0}
                  textAnchor="middle"
                    height={60}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                />
                <YAxis 
                    width={60}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                    tickMargin={10}
                />
                <Tooltip 
                    formatter={(value, name) => [
                      name === 'avgLatency' ? `${value}ms` : 
                      name === 'avgTokens' ? `${value} tokens` : 
                      name === 'totalQueries' ? `${value} queries` : value,
                      name === 'avgLatency' ? 'Avg Latency' :
                      name === 'avgTokens' ? 'Avg Tokens' :
                      name === 'totalQueries' ? 'Total Queries' : name
                    ]}
                    labelFormatter={(label) => `Model: ${label}`}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E5E7EB', 
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(10px)'
                    }}
                    cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }}
                />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}
                  />
                  <Bar dataKey="avgLatency" name="Average Latency (ms)" radius={[8, 8, 0, 0]} barSize={40}>
                    {queryData?.modelPerformance?.map((entry, index) => (
                      <Cell key={`model-cell-${index}`} fill={`url(#modelBarGradient${(index % 4) + 1})`} />
                    ))}
                  </Bar>
                </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Query Search and List */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Recent Queries</h3>
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search queries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Query
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Latency
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQueries.slice(0, 5).map((query, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="truncate max-w-md" title={query.prompt}>
                      {query.prompt}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.model}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.inputTokens + query.outputTokens}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.latency.toFixed(0)}ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QueryAnalytics; 